import { buyWyrmRetry, grantWyrmKill } from "@highmarch/app";
import type { LevelCatalog, WyrmGrant } from "@highmarch/app";
import type { GearItem, LevelSim } from "@highmarch/domain";
import { systemClock } from "../infra/adapters";
import type { WyrmHooks } from "../level/wyrmMoment";
import { skinsLink } from "../ui/hub/skinsLink";
import { skinTarget } from "../ui/wyrmModel";
import type { WyrmStore } from "../ui/wyrmTopUp";
import { economyLink } from "../ui/hub/economyLink";
import { dealsLink } from "../ui/deals/dealsLink";
import { gemPackCtx } from "../ui/deals/dealsPages";

/**
 * The Starfall Wyrm's reward and paid retry (M40, app wyrm.ts), for the battle's composition (boot/levelFlow.ts →
 * MetaFlow): the grant is banked and saved the tick the Wyrm falls (MetaFlow.watchBattle's end-of-tick hook, like the
 * rare's chest), so a closed page or a later loss keeps it; a retry is charged and saved before its command is sent.
 * The choice's top-up buys gem packs through the game's PurchaseService as screen "wyrmChoice" (the one in-battle
 * exception to the hub-only rule, domain gemPacksAllowed); none when this build has no store.
 */
export class WyrmBank {
  private grant: WyrmGrant | null = null;
  private gear: readonly GearItem[] = [];

  constructor(private readonly catalog: LevelCatalog, private readonly persist: () => void) {}

  /** A new battle: nothing granted yet. */
  reset(): void { this.grant = null; this.gear = []; }

  /** The kill's grant: granted once (app grantWyrmKill, idempotent per Wyrm) and saved; null until it falls. */
  now(sim: LevelSim): WyrmGrant | null {
    if (this.grant !== null || sim.wyrmRun?.outcome !== "killed") return this.grant;
    this.grant = grantWyrmKill(this.catalog, sim, systemClock.utcNowSeconds());
    if (this.grant?.kind === "chest") this.gear = this.grant.gear;
    if (this.grant?.fresh) this.persist();
    return this.grant;
  }

  /** What the moment needs from outside the level. */
  hooks(sim: LevelSim): WyrmHooks {
    return {
      grant: () => this.now(sim),
      gear: () => this.gear,
      buyRetry: () => {
        const r = buyWyrmRetry(this.catalog, sim, systemClock.utcNowSeconds());
        if (r === true) this.persist();
        return r;
      },
      gems: () => this.catalog.economy.wallet.balance("gems"),
      equip: (skinId) => {
        const link = skinsLink();
        if (!link) return "not here";
        const t = skinTarget(skinId);
        const r = t.kind === "tower" ? link.cosmetics.equip("tower", skinId, t.towerId) : link.cosmetics.equip("fortress", skinId);
        if (r === true) link.changed();                        // saved, and worn on the renderer (live towers re-dress)
        return r;
      },
      store: () => wyrmStore(),
    };
  }
}

/** The gem store the Wyrm's top-up opens (PurchaseService, as on the Shop sheet), or null without one. */
function wyrmStore(): WyrmStore | null {
  const link = economyLink(), p = link?.purchases ?? null;
  if (!link || !p) return null;
  const deals = dealsLink()?.deals;
  const packs = gemPackCtx(link.catalog.balance.commerce.shop.gemPacks, (items, cents) => deals?.value(items, cents) ?? { percent: 0, show: false }, (cents) => deals?.vipPointsFor(cents) ?? 0);
  return {
    sandbox: link.storeKind === "sandbox",
    packs: () => packs,
    prices: async () => new Map((await p.products()).map(({ pack, product }) => [pack.id, product.displayPrice])),
    buy: (id) => p.buy(id, "wyrmChoice"),
  };
}
